import { EFrequency } from '@/context/borrower';
import { store } from '@/context/store';
import { getNumberOfKids } from '@/utils/getNumberOfKids';
import { getMonthlyAmount } from '@/utils/paymentHelper';

import { postUrl } from '../fetch';
import { trackedServiceCall } from '../uniqueIDTransferHelper';

import { IServicingPostBody, IServicingReturn } from './fetchServicingTypes';

export interface IServiceabilityResult {
    isServicing: boolean;
    lenders: IServicingReturn;
    hasLenders: boolean;
}

export type IServicing = {
    RESULT: boolean;
};

export async function fetchServicing(): Promise<boolean> {
    // ***** Redux State *****
    const state = store.getState();

    // Store
    const shareExpenses = state.borrowerReducer.expenses.expensesShared;
    const billsFrequency =
        state.borrowerReducer.expenses.billsPaidFrequency ?? EFrequency.MONTHLY;
    const generalLiving = state.borrowerReducer.expenses.general;
    const mortgageRent = state.borrowerReducer.expenses.mortgageRent;
    const transportVehicle = state.borrowerReducer.expenses.transportVehicle;
    const education = state.borrowerReducer.expenses.education;
    const healthcare = state.borrowerReducer.expenses.healthcare;
    const otherLoans = state.borrowerReducer.expenses.otherLoans;
    const paidFrequency = state.borrowerReducer.income.paidFrequency;
    const amountPaid = state.borrowerReducer.income.paidAmount;
    const otherIncome = state.borrowerReducer.income.otherIncome;
    const sourceOfOtherIncome = state.borrowerReducer.income.otherIncomeSource;

    // ***** Constants *****
    const url = '/api/serviceability';
    const amountPaidMonthly = getMonthlyAmount(paidFrequency, amountPaid);
    const otherIncomeMonthly = getMonthlyAmount(paidFrequency, otherIncome);
    const generalLivingMonthly = getMonthlyAmount(
        billsFrequency,
        generalLiving
    );
    const transportVehicleMonthly = getMonthlyAmount(
        billsFrequency,
        transportVehicle
    );
    const educationMonthly = getMonthlyAmount(billsFrequency, education);
    const healthcareMonthly = getMonthlyAmount(billsFrequency, healthcare);
    const otherLoansMonthly = getMonthlyAmount(billsFrequency, otherLoans);
    const mortgageRentalMonthly = getMonthlyAmount(
        billsFrequency,
        mortgageRent
    );
    console.log('mortgageRentalMonthly', mortgageRentalMonthly);
    console.log('billsFrequency ', billsFrequency);
    console.log('mortgageRent', mortgageRent);
    const numberOfKids = getNumberOfKids();

    // ***** Body *****
    const body: IServicingPostBody = {
        monthlyRepayment: 300, //todo: get this from the store
        amountPaidInBankAccountMonthly: amountPaidMonthly,
        country: state.displayReducer.partner.country || 'AU',
        residencyExpenses: mortgageRentalMonthly,
        transportAndVehicleExpenses: transportVehicleMonthly,
        otherOngoingLoansExpenses: otherLoansMonthly,
        generalLivingExpenses: generalLivingMonthly,
        educationExpenses: educationMonthly,
        healthcareExpenses: healthcareMonthly,
        numberOfDependants: numberOfKids,
        amountOfAnyOtherIncomeMonthly: otherIncomeMonthly,
        sourceOfOtherIncome: sourceOfOtherIncome,
        isHouseholdExpensesShared: shareExpenses ? true : false,
    };

    const response = await trackedServiceCall('serviceability', () =>
        postUrl<IServicingReturn>(url, body)
    );

    // Check if any servicing lenders are found in the response
    for (const key in response) {
        if (Object.prototype.hasOwnProperty.call(response, key)) {
            const lender = response[key];
            if (lender.isServicing) {
                return true;
            }
        }
    }

    return false;
}

export async function fetchServiceabilityWithDetails(): Promise<IServiceabilityResult> {
    // ***** Redux State *****
    const state = store.getState();

    // Store
    const shareExpenses = state.borrowerReducer.expenses.expensesShared;
    const billsFrequency =
        state.borrowerReducer.expenses.billsPaidFrequency ?? EFrequency.MONTHLY;
    const generalLiving = state.borrowerReducer.expenses.general;
    const mortgageRent = state.borrowerReducer.expenses.mortgageRent;
    const transportVehicle = state.borrowerReducer.expenses.transportVehicle;
    const education = state.borrowerReducer.expenses.education;
    const healthcare = state.borrowerReducer.expenses.healthcare;
    const otherLoans = state.borrowerReducer.expenses.otherLoans;
    const paidFrequency = state.borrowerReducer.income.paidFrequency;
    const amountPaid = state.borrowerReducer.income.paidAmount;
    const otherIncome = state.borrowerReducer.income.otherIncome;
    const sourceOfOtherIncome = state.borrowerReducer.income.otherIncomeSource;

    // Get loan details from partner config for monthly repayment calculation
    const loanAmount = state.borrowerReducer.loan.purchasePrice;
    const loanTerm = state.displayReducer.partner.loanTerm || 5; // Default to 5 years
    const loanBalloon = state.displayReducer.partner.loanBalloon || 0; // Default to 0%

    // ***** Constants *****
    const url = '/api/serviceability';
    const amountPaidMonthly = getMonthlyAmount(paidFrequency, amountPaid);
    const otherIncomeMonthly = getMonthlyAmount(paidFrequency, otherIncome);
    const generalLivingMonthly = getMonthlyAmount(
        billsFrequency,
        generalLiving
    );
    const transportVehicleMonthly = getMonthlyAmount(
        billsFrequency,
        transportVehicle
    );
    const educationMonthly = getMonthlyAmount(billsFrequency, education);
    const healthcareMonthly = getMonthlyAmount(billsFrequency, healthcare);
    const otherLoansMonthly = getMonthlyAmount(billsFrequency, otherLoans);
    const mortgageRentalMonthly = getMonthlyAmount(
        billsFrequency,
        mortgageRent
    );
    const numberOfKids = getNumberOfKids();

    // Calculate monthly repayment based on loan amount, term, and balloon
    const monthlyRepayment =
        loanAmount && loanTerm
            ? Math.round(
                  (loanAmount * (1 + loanBalloon / 100)) / (loanTerm * 12)
              )
            : 300; // Fallback to 300 if calculation fails

    // ***** Body *****
    const body: IServicingPostBody = {
        monthlyRepayment,
        amountPaidInBankAccountMonthly: amountPaidMonthly,
        residencyExpenses: mortgageRentalMonthly,
        transportAndVehicleExpenses: transportVehicleMonthly,
        otherOngoingLoansExpenses: otherLoansMonthly,
        generalLivingExpenses: generalLivingMonthly,
        educationExpenses: educationMonthly,
        healthcareExpenses: healthcareMonthly,
        numberOfDependants: numberOfKids,
        amountOfAnyOtherIncomeMonthly: otherIncomeMonthly,
        sourceOfOtherIncome: sourceOfOtherIncome,
        isHouseholdExpensesShared: shareExpenses ? true : false,
    };

    const response = await trackedServiceCall('serviceability', () =>
        postUrl<IServicingReturn>(url, body)
    );

    // Check if any servicing lenders are found in the response
    let isServicing = false;

    // check for nz if nz
    if (state.displayReducer.partner.country === 'NZ') {
        if (response['NZGeneralLender'].isServicing) {
            isServicing = true;
        }
    } else {
        if (response['generalLender'].isServicing) {
            isServicing = true;
        }
    }

    return {
        isServicing,
        lenders: response,
        hasLenders: false, // hasLenders will be set by quick quote result in ExpenseButtons
    };
}
